import { useState } from 'react';
import { Users, UserCheck, UserX, FolderTree } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { KPICard } from '@/components/shared/KPICard';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { usePrincipals } from '@/hooks/usePrincipals';
import { useGroups } from '@/hooks/useGroups';
import { formatNumber } from '@/lib/formatters';
import type { Principal } from '@/types/principal';
import { UsersTab } from './UsersTab';
import { GroupsTab } from './GroupsTab';
import { PrincipalDetailDialog } from './PrincipalDetailDialog';

type DirectoryTab = 'users' | 'groups';

/**
 * Directory — org-wide view of users (principals) and groups provisioned
 * from identity providers (IDP sync and SCIM push). Deactivated users stay
 * visible under the default "All statuses" filter, so offboarding is
 * auditable at a glance.
 */
export function DirectoryPage() {
  const [activeTab, setActiveTab] = useState<DirectoryTab>('users');
  const [groupFilter, setGroupFilter] = useState<string | null>(null);
  const [selectedPrincipal, setSelectedPrincipal] = useState<Principal | null>(null);

  // KPI tiles — cheap per_page:1 queries; totals come from pagination meta.
  const totalQuery = usePrincipals({ status: 'all', per_page: 1 });
  const activeQuery = usePrincipals({ status: 'active', per_page: 1 });
  const inactiveQuery = usePrincipals({ status: 'inactive', per_page: 1 });
  const groupsCountQuery = useGroups({ per_page: 1 });

  const kpi = (q: { data?: { meta: { pagination: { total: number } } } }) =>
    q.data ? formatNumber(q.data.meta.pagination.total) : '—';

  const openUsersTabForGroup = (groupName: string) => {
    setGroupFilter(groupName);
    setActiveTab('users');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        eyebrow="Connect"
        title="Directory"
        subtitle="Users and groups provisioned from your identity providers."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard
          icon={<Users className="h-4 w-4" />}
          label="Total users"
          value={kpi(totalQuery)}
          accent="primary"
        />
        <KPICard
          icon={<UserCheck className="h-4 w-4" />}
          label="Active"
          value={kpi(activeQuery)}
          accent="allow"
        />
        <KPICard
          icon={<UserX className="h-4 w-4" />}
          label="Deactivated"
          value={kpi(inactiveQuery)}
          accent="warn"
        />
        <KPICard
          icon={<FolderTree className="h-4 w-4" />}
          label="Groups"
          value={kpi(groupsCountQuery)}
        />
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as DirectoryTab)}>
        <TabsList>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="groups">Groups</TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-4">
          <UsersTab
            groupFilter={groupFilter}
            onClearGroupFilter={() => setGroupFilter(null)}
            onSelectPrincipal={setSelectedPrincipal}
          />
        </TabsContent>
        <TabsContent value="groups" className="mt-4">
          <GroupsTab onSelectGroup={openUsersTabForGroup} />
        </TabsContent>
      </Tabs>

      <PrincipalDetailDialog
        principal={selectedPrincipal}
        onClose={() => setSelectedPrincipal(null)}
      />
    </div>
  );
}
